import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Text,
  Spinner,
  useToast,
  Button,
  Flex,
  Checkbox,
  Tooltip,
  IconButton
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const AdminSecurity = () => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [filters, setFilters] = useState({
    user_type: '',
    endpoint: '',
    method: '',
    status: '',
    limit: 100
  });
  const filtersRef = useRef(filters);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchLogs = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      const currentFilters = filtersRef.current;
      
      if (currentFilters.user_type) params.append('user_type', currentFilters.user_type);
      if (currentFilters.endpoint) params.append('endpoint', currentFilters.endpoint);
      if (currentFilters.method) params.append('method', currentFilters.method);
      if (currentFilters.status) params.append('status', currentFilters.status);
      params.append('limit', currentFilters.limit);

      const response = await fetch(`${apiBaseUrl}/api/admin/access-logs?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else {
        throw new Error(t('security_error_cargar'));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [accessToken, apiBaseUrl, t, toast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresco cada 60 segundos con contador
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          // Cuando llega a 0, refrescar y resetear
          fetchLogs();
          return 60;
        }
        return prev - 1;
      });
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, fetchLogs]);

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'green';
    if (status >= 300 && status < 400) return 'blue';
    if (status >= 400 && status < 500) return 'orange';
    if (status >= 500) return 'red';
    return 'gray';
  };

  const getUserTypeColor = (type) => {
    const colors = {
      'administrador': 'purple',
      'conductor': 'teal',
      'cliente': 'cyan',
      'anonimo': 'gray'
    };
    return colors[type] || 'gray';
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Text as="span" ml={1} opacity={0.5}>↕️</Text>;
    }
    return sortConfig.direction === 'asc' 
      ? <Text as="span" ml={1}>⬆️</Text>
      : <Text as="span" ml={1}>⬇️</Text>;
  };

  const sortedLogs = useMemo(() => {
    const sorted = [...logs].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Sort based on type
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number') {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }

      // For dates
      if (sortConfig.key === 'timestamp') {
        return sortConfig.direction === 'asc'
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      return 0;
    });

    return sorted;
  }, [logs, sortConfig]);

  return (
    <VStack spacing={6} align="stretch" p={6} bg="gray.50" minH="100vh" className="admin-page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" className="admin-page-header">
        <HStack spacing={2}>
          <Tooltip 
            label={autoRefreshEnabled ? "Desactivar auto refresh" : "Activar auto refresh"}
            placement="top"
            hasArrow
          >
            <Checkbox
              isChecked={autoRefreshEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                setAutoRefreshEnabled(newValue);
                if (newValue) {
                  setRefreshCountdown(60);
                }
              }}
              colorScheme="purple"
              size="lg"
            />
          </Tooltip>
          <IconButton
            icon={<span style={{ fontSize: '18px' }}>&nbsp;🔄 ({refreshCountdown})&nbsp;</span>}
            onClick={() => {
              fetchLogs();
              setRefreshCountdown(60);
            }}
            colorScheme="purple"
            size="sm"
            variant="outline"
            aria-label="Refrescar logs"
            title={`Refrescar lista de logs (auto-refresco en ${refreshCountdown}s)`}
          />
          <Text fontSize="xl" fontWeight="bold" color="gray.800" className="admin-page-title">
            {t('menu_admin_seguridad')}
          </Text>
        </HStack>
      </Box>

      {/* Filtros */}
      <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" className="security-filters" borderWidth="1px" borderColor="gray.200">
        <HStack spacing={4} flexWrap="wrap">
          <Box flex="1" minW="150px">
            <Text fontSize="sm" fontWeight="600" mb={1} color="gray.700">{t('security_tipo_usuario')}</Text>
            <Select
              value={filters.user_type}
              onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
              placeholder={t('security_todos')}
              size="sm"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: 'purple.400' }}
            >
              <option value="administrador">{t('security_administrador')}</option>
              <option value="conductor">{t('security_conductor')}</option>
              <option value="cliente">{t('security_cliente')}</option>
              <option value="anonimo">{t('security_anonimo')}</option>
            </Select>
          </Box>

          <Box flex="1" minW="150px">
            <Text fontSize="sm" fontWeight="600" mb={1} color="gray.700">{t('security_metodo_http')}</Text>
            <Select
              value={filters.method}
              onChange={(e) => setFilters({ ...filters, method: e.target.value })}
              placeholder={t('security_todos')}
              size="sm"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: 'purple.400' }}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </Select>
          </Box>

          <Box flex="1" minW="150px">
            <Text fontSize="sm" fontWeight="600" mb={1} color="gray.700">{t('security_estado_http')}</Text>
            <Input
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder="200, 404, 500..."
              size="sm"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: 'purple.400' }}
            />
          </Box>

          <Box flex="1" minW="150px">
            <Text fontSize="sm" fontWeight="600" mb={1} color="gray.700">{t('security_endpoint')}</Text>
            <Input
              value={filters.endpoint}
              onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })}
              placeholder="/api/..."
              size="sm"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: 'purple.400' }}
            />
          </Box>

          <Box flex="0" minW="100px">
            <Text fontSize="sm" fontWeight="600" mb={1} color="gray.700">{t('security_limite')}</Text>
            <Select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
              size="sm"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: 'purple.400' }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </Select>
          </Box>

          <Flex align="flex-end">
            <Button
              onClick={fetchLogs}
              colorScheme="purple"
              size="sm"
              boxShadow="md"
              className="admin-button-primary"
            >
              🔍 {t('security_filtrar')}
            </Button>
          </Flex>

          <Flex align="flex-end">
            <Button
              onClick={fetchLogs}
              variant="outline"
              colorScheme="purple"
              size="sm"
              className="admin-button-secondary"
            >
              🔄 {t('security_recargar')}
            </Button>
          </Flex>
        </HStack>
      </Box>

      {/* Tabla de logs */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.200">
        {loading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="xl" color="purple.500" thickness="4px" />
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm" className="admin-table">
              <Thead bg="purple.600">
                <Tr>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('timestamp')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_timestamp')}
                      {getSortIcon('timestamp')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('user_id')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_usuario')}
                      {getSortIcon('user_id')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('user_type')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_tipo')}
                      {getSortIcon('user_type')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('sub_rol')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_sub_rol')}
                      {getSortIcon('sub_rol')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('method')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_metodo')}
                      {getSortIcon('method')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('endpoint')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_endpoint')}
                      {getSortIcon('endpoint')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('response_status')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_estado')}
                      {getSortIcon('response_status')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('duration_ms')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_duracion')}
                      {getSortIcon('duration_ms')}
                    </Flex>
                  </Th>
                  <Th 
                    color="white" 
                    fontWeight="bold" 
                    borderColor="purple.500"
                    cursor="pointer"
                    onClick={() => handleSort('ip_address')}
                    _hover={{ bg: 'purple.700' }}
                    userSelect="none"
                  >
                    <Flex align="center">
                      {t('security_ip')}
                      {getSortIcon('ip_address')}
                    </Flex>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedLogs.length === 0 ? (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={6}>
                      <Text color="gray.500">{t('security_no_registros')}</Text>
                    </Td>
                  </Tr>
                ) : (
                  sortedLogs.map((log, index) => (
                    <Tr 
                      key={log.id} 
                      _hover={{ bg: 'purple.50' }} 
                      bg={index % 2 === 0 ? 'white' : 'gray.50'}
                      transition="background-color 0.2s"
                    >
                      <Td fontSize="xs" color="gray.700" borderColor="gray.200">
                        {new Date(log.timestamp).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </Td>
                      <Td fontSize="xs" maxW="150px" isTruncated color="gray.700" borderColor="gray.200">
                        {log.user_id ? log.user_id.substring(0, 8) + '...' : '-'}
                      </Td>
                      <Td borderColor="gray.200">
                        <Badge colorScheme={getUserTypeColor(log.user_type)} fontSize="xs">
                          {log.user_type}
                        </Badge>
                      </Td>
                      <Td fontSize="xs" color="gray.700" borderColor="gray.200">{log.sub_rol || '-'}</Td>
                      <Td borderColor="gray.200">
                        <Badge
                          colorScheme={
                            log.method === 'GET' ? 'blue' :
                            log.method === 'POST' ? 'green' :
                            log.method === 'PUT' || log.method === 'PATCH' ? 'yellow' :
                            log.method === 'DELETE' ? 'red' : 'gray'
                          }
                          fontSize="xs"
                        >
                          {log.method}
                        </Badge>
                      </Td>
                      <Td fontSize="xs" maxW="250px" isTruncated title={log.endpoint} color="gray.700" borderColor="gray.200">
                        {log.endpoint}
                      </Td>
                      <Td borderColor="gray.200">
                        <Badge colorScheme={getStatusColor(log.response_status)} fontSize="xs">
                          {log.response_status}
                        </Badge>
                      </Td>
                      <Td fontSize="xs" color="gray.700" borderColor="gray.200">{formatDuration(log.duration_ms)}</Td>
                      <Td fontSize="xs" color="gray.700" borderColor="gray.200">{log.ip_address || '-'}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {sortedLogs.length > 0 && (
        <Text fontSize="sm" color="gray.600" textAlign="center" fontWeight="500">
          {t('security_mostrando_registros', { count: sortedLogs.length })}
        </Text>
      )}
    </VStack>
  );
};

export default AdminSecurity;
